import alt from 'components/Dispatcher';
import generateAsyncActions from 'utils/generateAsyncActions';
import TriggerSource from 'sources/triggers/TriggersSource';

class TriggerRuleActions {
  constructor() {
    this.generateActions(
      ...generateAsyncActions(
        'fetchTriggerRules',
        'createTriggerRule',
        'updateTriggerRule',
        'removeTriggerRule',
        'fetchTriggerRuleActions',
        'createTriggerRuleAction',
        'fetchTriggerRule',
        'updateTriggerRulePermissions',
        'updateTriggerRuleAction',
        'updateTriggerTrend',
        'removeTriggerRuleAction',
        'fetchTriggerTrends',
        'createTriggerTrend',
        'fetchTriggerRulePermissions',
        'fetchAntiRules',
        'fetchTriggerRuleTags',
        'updateTriggerRuleTags',
        'cloneTriggerRule',
        'updateTriggerName'
      )
    );
  }

  fetchTriggerRules() {
    return dispatch => {
      dispatch();
      return TriggerSource.fetchTriggerRules().then(
        this.fetchTriggerRulesSuccess,
        this.fetchTriggerRulesError
      );
    };
  }

  fetchTriggerRulePermissions(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.fetchTriggerRulePermissions(ruleId).then(
        permissions => this.fetchTriggerRulePermissionsSuccess(ruleId, permissions),
        error => this.fetchTriggerRulePermissionsError(ruleId, error)
      );
    };
  }

  updateTriggerRulePermissions(ruleId, permissions) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.updateTriggerRulePermissions(ruleId, permissions).then(
        this.updateTriggerRulePermissionsSuccess,
        this.updateTriggerRulePermissionsError
      );
    };
  }

  fetchTriggerRule(id) {
    return dispatch => {
      dispatch(id);
      return TriggerSource.fetchTriggerRule(id).then(
        this.fetchTriggerRuleSuccess,
        this.fetchTriggerRuleError
      );
    };
  }

  createTriggerRule(trigger) {
    return dispatch => {
      dispatch(trigger);
      return TriggerSource.createTriggerRule(trigger).then(this.createTriggerRuleSuccess, error => {
        this.createTriggerRuleError(error);
        throw error;
      });
    };
  }

  cloneTriggerRule(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.cloneTriggerRule(ruleId).then(
        // I'm just going to reuse the same store methods
        this.createTriggerRuleSuccess,
        this.createTriggerRuleError
      );
    };
  }

  updateTriggerRule(ruleId, trigger) {
    return dispatch => {
      dispatch();
      return TriggerSource.updateTriggerRule(ruleId, trigger).then(
        this.updateTriggerRuleSuccess,
        this.updateTriggerRuleError
      );
    };
  }

  updateTriggerRuleDamping(ruleId, damping) {
    return dispatch => {
      dispatch();
      return TriggerSource.updateTriggerRuleDamping(ruleId, damping).then(
        this.updateTriggerRuleSuccess,
        this.updateTriggerRuleError
      );
    };
  }

  removeTriggerRule(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.removeTriggerRule(ruleId).then(
        () => this.removeTriggerRuleSuccess(ruleId),
        this.removeTriggerRuleError
      );
    };
  }

  fetchTriggerRuleActions(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.fetchTriggerRuleActions(ruleId).then(
        results => this.fetchTriggerRuleActionsSuccess(ruleId, results),
        this.fetchTriggerRuleActionsError
      );
    };
  }

  createTriggerRuleAction(ruleId, triggerRuleAction) {
    return dispatch => {
      dispatch(ruleId, triggerRuleAction);
      return TriggerSource.createTriggerRuleAction(ruleId, triggerRuleAction).then(
        this.createTriggerRuleActionSuccess,
        this.createTriggerRuleActionError
      );
    };
  }

  upsertTriggerRuleAction(ruleId, action) {
    return dispatch => {
      dispatch([ruleId, action]);
      if (action.id) {
        return TriggerSource.updateTriggerRuleAction(ruleId, action.id, action)
          .then(this.updateTriggerRuleActionSuccess)
          .catch(e => {
            this.updateTriggerRuleActionError(e);
            throw e;
          });
      } else {
        return TriggerSource.createTriggerRuleAction(ruleId, action).then(
          this.createTriggerRuleActionSuccess,
          this.createTriggerRuleActionError
        );
      }
    };
  }

  updateTriggerRuleAction(ruleId, actionId, triggerRuleAction) {
    return dispatch => {
      dispatch([ruleId, actionId, triggerRuleAction]);
      return TriggerSource.updateTriggerRuleAction(ruleId, actionId, triggerRuleAction)
        .then(this.updateTriggerRuleActionSuccess)
        .catch(e => {
          this.updateTriggerRuleActionError(e);
          throw e;
        });
    };
  }

  removeTriggerRuleAction(ruleId, actionId) {
    return dispatch => {
      dispatch([ruleId, actionId]);
      return TriggerSource.removeTriggerRuleAction(ruleId, actionId)
        .then(this.removeTriggerRuleActionSuccess)
        .catch(e => {
          this.removeTriggerRuleActionError(e);
          throw e;
        });
    };
  }

  updateTriggerTrend(ruleId, triggerTrend) {
    return dispatch => {
      dispatch([ruleId, triggerTrend]);
      return TriggerSource.updateTriggerTrend(ruleId, triggerTrend)
        .then(this.updateTriggerTrendSuccess)
        .catch(e => {
          this.updateTriggerTrendError(e);
          throw e;
        });
    };
  }

  fetchTriggerTrends(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.getTriggerTrends(ruleId).then(
        results => this.fetchTriggerTrendsSuccess(ruleId, results),
        this.fetchTriggerTrendsError
      );
    };
  }

  createTriggerTrend(ruleId, trend) {
    return dispatch => {
      dispatch([ruleId, trend]);
      return TriggerSource.createTriggerTrend(ruleId, trend).then(
        this.createTriggerTrendSuccess,
        error => {
          this.createTriggerTrendError(error);
          throw error;
        }
      );
    };
  }

  fetchAntiRules(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.fetchAntiRules(ruleId).then(
        this.fetchAntiRulesSuccess,
        this.fetchAntiRulesError
      );
    };
  }

  fetchTriggerRuleTags(ruleId) {
    return dispatch => {
      dispatch(ruleId);
      return TriggerSource.fetchTriggerRuleTags(ruleId).then(
        tags => this.fetchTriggerRuleTagsSuccess(ruleId, tags),
        error => this.fetchTriggerRuleTagsError(ruleId, error)
      );
    };
  }

  updateTriggerRuleTags(ruleId, tags) {
    return dispatch => {
      dispatch([ruleId, tags]);
      return TriggerSource.updateTriggerRuleTags(ruleId, tags).then(
        tags => this.updateTriggerRuleTagsSuccess(ruleId, tags),
        error => this.updateTriggerRuleTagsError(ruleId, error)
      );
    };
  }

  updateTriggerName(ruleId, name) {
    return dispatch => {
      dispatch(ruleId, name);
      return TriggerSource.updateTriggerName(ruleId, name).then(
        this.updateTriggerNameSuccess,
        error => this.updateTriggerNameError(ruleId, error)
      );
    };
  }
}

export default alt.createActions(TriggerRuleActions);
